import React, { FC } from 'react';

import { DatePicker, Form, FormInstance, Input, Radio, Select } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import {
  CatalogType,
  CatalogVisibilityRule,
  OrderApprovalRule,
  ViewProductInCatalogSchema,
} from '@app/models/products';
import { stringToBoolean } from '@app/services/stringToBoolean';
import {
  GroupsSelect,
  OrderApprovalChainSelect,
  ProcessSelect,
  ProjectSelect,
  Upload,
  UsersSelect,
  WarehouseSelect,
} from '@components/AntFields';
import { FormTitle } from '@components/FormTitle';
import { Paper } from '@components/Paper';

import { useProductModal } from '@pages/Catalogs/providers';

import { ProductsModal } from './components/ProductsModal';

import { ValidationSchema } from './data/validation';
import { IFormValues } from './interfaces';

interface IProps {
  initialValues?: Partial<IFormValues>;
  initial_user_group_options?: { label: string; value: string }[];
  initial_order_approval_chain_options?: { label: string; value: string }[];
  onFinish: (formValues: IFormValues) => void;
  form: FormInstance;
  initialProducts?: ViewProductInCatalogSchema[];
  areDateFieldsDisabled?: boolean;
}

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

const normalizeVisibleUsers = (
  value?: IFormValues['visible_to_users'],
  prevValue?: IFormValues['visible_to_users']
) =>
  value?.map((user) => {
    const previousUser = prevValue?.find((prevUser) => prevUser.value === user.value);
    const label = typeof user.label === 'string' ? user.label : previousUser?.label;

    return {
      value: user.value,
      ...(label ? { label } : {}),
    };
  });

export const FormCatalog: FC<IProps> = (props) => {
  const validationT = useTranslation(NSKeys.validation);
  const catalogsT = useTranslation(NSKeys.catalogs);
  const { resultProducts, setResultProducts, onSelectChange } = useProductModal();

  const orderApprovalRule = Form.useWatch('order_approval_rule', props.form);
  const visibilityRule = Form.useWatch('visibility_rule', props.form);
  const processUid = Form.useWatch('process_uid', props.form);
  const warehouse = Form.useWatch('warehouse', props.form);
  const warehouseUid = warehouse?.value;
  const catalogType = Form.useWatch('catalog_type', props.form);
  const dateFieldTooltip = props.areDateFieldsDisabled ? catalogsT.t('form.dateFieldsDisabledTooltip') : '';

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  React.useEffect(() => {
    setResultProducts(props?.initialProducts || []);
    onSelectChange(props?.initialProducts || []);
  }, [props.initialProducts]);

  React.useEffect(() => {
    if (resultProducts.length) {
      props.form.setFieldValue(
        'product_uids',
        resultProducts.map((el) => el.uid)
      );
    } else {
      props.form.setFieldValue('product_uids', undefined);
    }
  }, [resultProducts]);

  const handleWareHouseChange = (value?: any) => {
    setResultProducts([]);
    onSelectChange([]);
    if (value && !catalogType) {
      props.form.setFieldValue('catalog_type', CatalogType.Manual);
    }
  };

  return (
    <Paper style={{ margin: 24, padding: '20px 24px' }}>
      <FormTitle title={catalogsT.t('form.groupInfo')} />
      <Form<IFormValues>
        form={props.form}
        initialValues={props?.initialValues}
        name="catalog_form"
        id="catalogForm"
        onFinish={props.onFinish}
        autoComplete="off"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}>
        <Form.Item<IFormValues> name="name" required label={catalogsT.t('form.nameLabel')} rules={[yupSync]}>
          <Input placeholder={catalogsT.t('form.namePlaceholder')} />
        </Form.Item>
        <Form.Item<IFormValues>
          name="start_date"
          tooltip={props.areDateFieldsDisabled ? dateFieldTooltip : null}
          label={catalogsT.t('form.startDateLabel')}
          normalize={(value) => (value ? value.toISOString() : null)}
          getValueProps={(value) => ({ value: value ? dayjs.utc(value).local() : null })}
          rules={[yupSync]}>
          <DatePicker
            showTime
            disabled={props.areDateFieldsDisabled}
            format="YYYY-MM-DD HH:mm:ss"
            placeholder={catalogsT.t('form.startDatePlaceholder')}
          />
        </Form.Item>
        <Form.Item<IFormValues>
          name="end_date"
          tooltip={props.areDateFieldsDisabled ? dateFieldTooltip : null}
          label={catalogsT.t('form.endDateLabel')}
          normalize={(value) => (value ? value.toISOString() : null)}
          getValueProps={(value) => ({ value: value ? dayjs.utc(value).local() : null })}
          rules={[yupSync]}>
          <DatePicker
            showTime
            disabled={props.areDateFieldsDisabled}
            format="YYYY-MM-DD HH:mm:ss"
            placeholder={catalogsT.t('form.endDatePlaceholder')}
          />
        </Form.Item>

        <Form.Item<IFormValues> name="process_uid" label={catalogsT.t('form.processLabel')} rules={[yupSync]}>
          <ProcessSelect placeholder={catalogsT.t('form.processPlaceholder')} allowClear />
        </Form.Item>

        {processUid && (
          <Form.Item<IFormValues> name="project_uid" label={catalogsT.t('form.projectLabel')} rules={[yupSync]}>
            <ProjectSelect
              key={processUid}
              process_uid={processUid}
              placeholder={catalogsT.t('form.projectLabel')}
              defaultOpen={false}
            />
          </Form.Item>
        )}

        <Form.Item<IFormValues>
          name="show_multiplicity_indicator"
          label={catalogsT.t('form.showMultiplicityIndicatorLabel')}
          rules={[yupSync]}>
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={true}>{catalogsT.t('form.showMultiplicityIndicator.true')}</Radio.Button>
            <Radio.Button value={false}>{catalogsT.t('form.showMultiplicityIndicator.false')}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item<IFormValues>
          name="media"
          label={catalogsT.t('form.coverLabel')}
          valuePropName="fileList"
          getValueFromEvent={normFile}>
          <Upload
            apiPath="products/catalogs/upload-cover"
            listType="picture-card"
            name="media"
            multiple={false}
            showUploadList={{ showRemoveIcon: false }}
            maxCount={1}
          />
        </Form.Item>

        {stringToBoolean(process.env.REACT_APP_FEATURE_WAREHOUSES) && (
          <>
            <FormTitle title={catalogsT.t('form.groupWarehouse')} />
            <Form.Item<IFormValues> name="warehouse" label={catalogsT.t('form.warehouseLabel')} rules={[yupSync]}>
              <WarehouseSelect
                placeholder={catalogsT.t('form.warehousePlaceholder')}
                allowClear
                onChange={handleWareHouseChange}
              />
            </Form.Item>

            <Form.Item<IFormValues>
              style={{ display: warehouseUid ? 'block' : 'none' }}
              name="catalog_type"
              label={catalogsT.t('form.catalogTypeLabel')}
              rules={[yupSync]}>
              <Radio.Group
                block
                options={(Object.keys(CatalogType) as Array<keyof typeof CatalogType>).map((key) => ({
                  label: `${catalogsT.t(`form.catalogTypeLabel-${CatalogType[key]}`)}`,
                  value: CatalogType[key],
                  disabled: CatalogType[key] !== CatalogType.Manual && CatalogType[key] !== CatalogType.Mirror,
                }))}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            {catalogType === CatalogType.Manual && (
              <Form.Item<IFormValues>
                name="product_uids"
                label={catalogsT.t('form.productUidsLabel')}
                rules={[yupSync]}>
                {/* <Input />*/}
                {catalogType === CatalogType.Manual && <ProductsModal warehouse_uid={warehouseUid} />}
              </Form.Item>
            )}
          </>
        )}

        <FormTitle title={catalogsT.t('form.groupInfo')} />
        <Form.Item<IFormValues> name="visibility_rule" label={catalogsT.t('form.visibilityLabel')} rules={[yupSync]}>
          <Select
            placeholder={catalogsT.t('form.visibilityPlaceholder')}
            options={(Object.keys(CatalogVisibilityRule) as Array<keyof typeof CatalogVisibilityRule>).map((key) => ({
              label: catalogsT.t(`form.visibility.${CatalogVisibilityRule[key]}` as any),
              value: CatalogVisibilityRule[key],
            }))}
          />
        </Form.Item>
        {visibilityRule === CatalogVisibilityRule.VisibleToUsers && (
          <Form.Item<IFormValues>
            name="visible_to_users"
            label={catalogsT.t('form.visibilityUsersLabel')}
            normalize={normalizeVisibleUsers}
            rules={[yupSync]}
            required>
            <UsersSelect
              labelInValue={true}
              mode="multiple"
              placeholder={catalogsT.t('form.visibilityUsersPlaceholder')}
            />
          </Form.Item>
        )}

        {visibilityRule === CatalogVisibilityRule.VisibleToGroups && (
          <Form.Item<IFormValues>
            name="visible_to_user_group_uids"
            label={catalogsT.t('form.visibilityGroupsLabel')}
            rules={[yupSync]}
            required>
            <GroupsSelect
              mode="multiple"
              initialOptions={props.initial_user_group_options}
              placeholder={catalogsT.t('form.visibilityGroupsPlaceholder')}
            />
          </Form.Item>
        )}

        <FormTitle title={catalogsT.t('form.groupOrder')} />
        <Form.Item<IFormValues> name="order_approval_rule" label={catalogsT.t('form.orderApprovalRuleLabel')}>
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={OrderApprovalRule.OrderApprovalChain}>
              {catalogsT.t('form.orderApprovalRule.order_approval_chain')}
            </Radio.Button>
            <Radio.Button value={OrderApprovalRule.NotNeeded}>
              {catalogsT.t('form.orderApprovalRule.not_needed')}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {orderApprovalRule === OrderApprovalRule.OrderApprovalChain && (
          <Form.Item<IFormValues>
            name="order_approval_chain_uid"
            label={catalogsT.t('form.orderApprovalChainLabel')}
            rules={[yupSync]}
            required>
            <OrderApprovalChainSelect
              placeholder={catalogsT.t('form.orderApprovalChainPlaceholder')}
              initialOptions={props.initial_order_approval_chain_options}
            />
          </Form.Item>
        )}
      </Form>
    </Paper>
  );
};
